import { ErrorCode } from '@repo/types'

/**
 * What a buyer reads when their scan does not complete the claim.
 *
 * ## The rule this file exists to enforce
 *
 * A buyer NEVER sees a raw error code, and never sees "something went wrong".
 * Every outcome here is genuinely different and needs a different thing said —
 * collapsing them into one generic message is named in the design as the
 * failure to avoid, because some of these cases have a path forward and others
 * do not, and the user cannot tell which they are in from a shrug.
 *
 * The sharpest instance is not a vague message but a WRONG one: until the API
 * distinguished them, a buyer who had already claimed their purchase and one
 * whose sale had been refunded both received the NOT_FOUND copy, which offers
 * to look again under a different checkout email. Neither of them mistyped
 * anything and neither can be helped by another address.
 *
 * ## Which copy is SETTLED and which is not
 *
 * Two are settled by the user and are reproduced VERBATIM below. Do not
 * paraphrase, tighten or "improve" them — they were written to a specific
 * frame (see `useLinkCheckoutEmail`: the account is the identity, the email is
 * only a lookup key) and a rewrite that reads better in isolation can quietly
 * reintroduce "your email is your account".
 *
 * The rest are marked `TODO(VRG-455)` and are plain, neutral placeholders.
 * They are honest about what happened and name the one thing the reader can do,
 * but they have not been through the user, so they must not be treated as final
 * — in particular `CERTIFICATE_NOT_RELEASED`, which is the one outcome where
 * the scanner may be acting in bad faith and where the wording carries a
 * support cost in either direction.
 */
export type PendingSaleFailureAction =
  /** Opens the checkout-email recovery step, in this same sheet. */
  | 'enter-checkout-email'
  /** mailto/contact the brand that recorded the sale. */
  | 'contact-brand'
  /** Return to the scan step and try again. */
  | 'try-again'

export interface PendingSaleFailureCopy {
  title: string
  body: string
  actions: PendingSaleFailureAction[]
  /** Marks copy that has NOT been confirmed by the user. */
  provisional?: boolean
}

const SETTLED: Partial<Record<ErrorCode, PendingSaleFailureCopy>> = {
  /*
   * SETTLED — user, 2026-08-27. Verbatim.
   *
   * The expected top failure of the whole design: ordered with a work address,
   * signed up with a personal one. Note the second sentence explains the
   * MECHANISM (purchases are matched by the checkout address) before offering
   * the fix, which is what makes the offer read as recovery rather than as an
   * account chore.
   *
   * ⚠️ This copy is TRUE ONLY when there is genuinely no record. It used to be
   * shown for a record the buyer had already claimed and for one that had been
   * voided as well, because the API's lookup filtered to `pending` and reported
   * all three as NOT_FOUND — so someone who already owned the item, or whose
   * order had been refunded, was invited to re-check under another email
   * address. Those two now have their own codes and their own copy below; this
   * one is unchanged and must stay verbatim.
   */
  [ErrorCode.PENDING_SALE_NOT_FOUND]: {
    title: "We couldn't find a purchase linked to your account.",
    body: "Purchases are matched using the email address used at checkout. If you ordered with a different one, enter it and we'll send you a code.",
    actions: ['enter-checkout-email', 'contact-brand'],
  },

  /*
   * SETTLED — user, 2026-08-27. Verbatim.
   *
   * Carries NO accusation, deliberately. The dominant causes are benign — two
   * people in a household buying the same product, a secondhand unit, a resold
   * return whose first owner never released it — and the brand can see the
   * purchase, which is why they are the destination rather than us.
   */
  [ErrorCode.PENDING_SALE_CERTIFICATE_ALREADY_OWNED]: {
    title: 'This item is already registered to another account.',
    body: '<Brand> can see your purchase and help sort this out.',
    actions: ['contact-brand'],
  },
}

const PROVISIONAL: Partial<Record<ErrorCode, PendingSaleFailureCopy>> = {
  /*
   * TODO(VRG-455): copy unconfirmed. The BRANCHING is not provisional — see the
   * note on NOT_FOUND above; only the wording is.
   *
   * The buyer already claimed this purchase. Nothing is wrong and nothing was
   * lost, so this must not read as an error, and above all it must not offer
   * the email-recovery path: they are not a buyer we failed to find, they are a
   * buyer who already owns the item. Points them at where it is instead.
   *
   * No actions, deliberately. "View your certificates" is where they are
   * already — the sheet opens from that list — so a button back to it would be
   * a loop. The sheet's own dismissal is the correct and only exit.
   */
  [ErrorCode.PENDING_SALE_ALREADY_CLAIMED]: {
    title: "You've already claimed this purchase.",
    body: "It's in your certificates. If you don't see it, pull down to refresh.",
    actions: [],
    provisional: true,
  },

  /*
   * TODO(VRG-455): copy unconfirmed. Again, the branching is not.
   *
   * The sale was retracted — refunded or cancelled (decision 1, requirement A).
   * The record survives as the audit of a retracted sale, which is why this is
   * reachable at all rather than reading as a missing purchase.
   *
   * States the fact without asserting a reason: "refunded" and "cancelled" are
   * both guesses from here, and telling someone their order was refunded when
   * it was cancelled for another reason is worse than saying neither. Only the
   * brand knows, and only they can undo it, so they are the single action.
   */
  [ErrorCode.PENDING_SALE_VOIDED]: {
    title: 'This purchase is no longer active.',
    body: '<Brand> cancelled this sale, so there is nothing to claim. They can tell you why and put it back if that was a mistake.',
    actions: ['contact-brand'],
    provisional: true,
  },

  /*
   * TODO(VRG-455): copy unconfirmed.
   *
   * A real item whose tag was missed in production. The buyer has done nothing
   * wrong and can do nothing about it, so the copy must not read as a failed
   * action on their part — it is the brand's problem and they are the only ones
   * who can fix it.
   */
  [ErrorCode.PENDING_SALE_TAG_HAS_NO_CERTIFICATE]: {
    title: "This tag isn't set up yet.",
    body: "We couldn't find a certificate for the tag you scanned. <Brand> can look into it for you.",
    actions: ['contact-brand', 'try-again'],
    provisional: true,
  },

  /*
   * TODO(VRG-455): copy unconfirmed, and this is the one to be most careful
   * with. It is the only outcome where the scanner may be acting in bad faith
   * (the shop-floor case), so the wording has a support cost whichever way it
   * leans: too pointed and it accuses an honest buyer who scanned a display
   * unit by mistake, too vague and it reads as a bug.
   *
   * This draft assumes the honest reading, because the honest reading is the
   * common one and an accusation is the more expensive mistake.
   */
  [ErrorCode.PENDING_SALE_CERTIFICATE_NOT_RELEASED]: {
    title: "This item hasn't shipped to a customer yet.",
    body: 'It looks like a display or stock item rather than the one sent to you. Scan the item you received, or ask <Brand> if you think this is wrong.',
    actions: ['try-again', 'contact-brand'],
    provisional: true,
  },

  /*
   * TODO(VRG-455): copy unconfirmed.
   *
   * A race with the buyer's own second tap — the compare-and-swap lost inside
   * this request, so the claim is landing as they read this. Nothing is broken
   * and nothing was lost, so the copy says so rather than treating it as an
   * error: the item is theirs and it is in their list.
   *
   * Its neighbour `PENDING_SALE_ALREADY_CLAIMED` is the same sentence about a
   * DIFFERENT moment — a claim completed earlier, found before any write. They
   * are two codes because the situations differ; if the copy ever converges,
   * merge them rather than keeping two names for one message.
   */
  [ErrorCode.PENDING_SALE_ALREADY_RESOLVED]: {
    title: 'This purchase has already been claimed.',
    body: "It's in your certificates. If you don't see it, pull down to refresh.",
    actions: [],
    provisional: true,
  },

  /*
   * TODO(VRG-455): copy unconfirmed, and pending a DESIGN decision rather than
   * just a wording one. The API refuses on purpose here: for a certificate that
   * participates in a group, whether an entitlement resolves against the child
   * or the parent is unsettled and belongs to VRG-231's group semantics. The
   * one thing this must not do is guess, so the copy cannot promise a fix.
   */
  [ErrorCode.PENDING_SALE_GROUPED_CERTIFICATE_UNSUPPORTED]: {
    title: "We can't claim this item automatically.",
    body: 'This item is part of a set, which needs a person to complete. <Brand> can finish it for you.',
    actions: ['contact-brand'],
    provisional: true,
  },
}

/**
 * The last resort — a failure with no code at all (network drop, a 500, a code
 * this build predates).
 *
 * It is still not "something went wrong": it names what was being attempted and
 * offers the two things that can help. A buyer holding a real item deserves at
 * minimum to know whether to try again or to write in.
 */
const UNKNOWN: PendingSaleFailureCopy = {
  title: "We couldn't finish claiming this item.",
  body: 'Please try scanning again. If it keeps happening, <Brand> can help.',
  actions: ['try-again', 'contact-brand'],
  provisional: true,
}

const FAILURE_COPY: Partial<Record<ErrorCode, PendingSaleFailureCopy>> = {
  ...SETTLED,
  ...PROVISIONAL,
}

/**
 * Resolve a failure to the copy a buyer reads, with `<Brand>` filled in.
 *
 * `brandName` falls back to "the brand" rather than leaving the placeholder on
 * screen — a literal `<Brand>` in shipped copy is worse than a generic noun,
 * and the company can legitimately be absent from a record whose company row
 * was removed.
 */
export const getPendingSaleFailureCopy = (
  code: ErrorCode | null | undefined,
  brandName?: string | null,
): PendingSaleFailureCopy => {
  const copy = (code && FAILURE_COPY[code]) || UNKNOWN
  const brand = brandName?.trim() || 'the brand'

  return {
    ...copy,
    title: copy.title.replace(/<Brand>/g, brand),
    body: copy.body.replace(/<Brand>/g, brand),
  }
}

/** The label on the contact action, so the brand is named on the button too. */
export const contactBrandLabel = (brandName?: string | null): string =>
  `Contact ${brandName?.trim() || 'the brand'}`
